"use client";

import * as React from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { MoonIcon, SunIcon, Loader2 } from 'lucide-react';
import { useTheme } from "next-themes";
import { toast } from "sonner";

import { User } from '@constructr-sdk/auth';
import { authClient } from "@constructr-sdk/auth/client";
import { Routes } from "@constructr-sdk/core/config/routes";
import { MediaQueries } from "@constructr-sdk/core/constants/media-queries";
import { getFilteredNavItems } from "@constructr-sdk/core/config/nav-items";
import { capabilityFlags } from "@constructr-sdk/core/config/capabilities-config";
import { useAppConfigContext } from "@constructr-sdk/core/config/context/app-config-context";
import { OrganizationsSelect } from '@constructr-sdk/modules/organization/components';
import { CopilotPopupComponent } from "@constructr-sdk/modules/aiassistant/presentation/default/components";
import { ScrollArea } from "@constructr-sdk/ui/components/scroll-area";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarTrigger,
  SidebarInset,
  SidebarProvider,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@constructr-sdk/ui/components/sidebar';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@constructr-sdk/ui/components/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@constructr-sdk/ui/components/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@constructr-sdk/ui/components/select";
import { BrandLogo } from "@constructr-sdk/ui/components/brand-logo";
import { useMediaQuery } from "@constructr-sdk/ui/hooks/use-media-query";
import { cn, getInitials } from '@constructr-sdk/ui/lib/utils';
import { useNavigation } from "@constructr-sdk/modules/context/navigation-context";

// ============================================================================
// User Avatar Component
// ============================================================================
export const UserAvatar = ({ profile }: { profile: User }) => (
  <Avatar className="size-7 rounded-full">
    <AvatarImage
      src={profile.image ?? undefined}
      alt={profile.name}
    />
    <AvatarFallback className="rounded-full">
      {getInitials(profile.name)}
    </AvatarFallback>
  </Avatar>
);

// ============================================================================
// User Settings Dropdown Component
// ============================================================================
export interface UserSettingsDropdownProps {
  profile?: User;
  trigger?: React.ReactNode;
  align?: "start" | "end" | "center";
  className?: string;
}

export function UserSettingsDropdown({
  profile,
  trigger,
  align = "start",
  className = "w-56",
}: UserSettingsDropdownProps): React.JSX.Element {
  const router = useRouter();
  const { theme, setTheme } = useTheme();

  const handleLogOut = async (): Promise<void> => {
    const { error } = await authClient.signOut({
      fetchOptions: {
        onError: (ctx) => {
          toast.error(ctx.error.message || "Couldn't log out");
        },
      },
    });

    if (!error) {
      // Force a full page reload to clear all client and server state
      window.location.href = Routes.Root;
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger || (
          profile && (
            <div className="flex items-center gap-2 cursor-pointer">
              <UserAvatar profile={profile} />
              <span className="truncate text-sm font-medium leading-none">
                {profile.name}
              </span>
            </div>
          )
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent className={className} align={align} forceMount>
        {profile && (
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="truncate text-sm font-medium leading-none">
                {profile.name}
              </p>
              <p className="text-xs leading-none text-muted-foreground">
                {profile.email}
              </p>
            </div>
          </DropdownMenuLabel>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {profile && (
            <DropdownMenuItem onClick={() => router.push(Routes.Profile)}>
              Settings
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            className="flex cursor-default flex-row justify-between !bg-transparent"
            onClick={(e) => e.stopPropagation()}
          >
            <p>Theme</p>
            <Select value={theme} onValueChange={setTheme}>
              <SelectTrigger className="h-[28px] w-[96px] px-2 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">
                  <span className="flex flex-row items-center gap-1 text-xs">
                    <SunIcon className="size-4 shrink-0 text-muted-foreground" />
                    Light
                  </span>
                </SelectItem>
                <SelectItem value="dark">
                  <span className="flex flex-row items-center gap-1 text-xs">
                    <MoonIcon className="size-4 shrink-0 text-muted-foreground" />
                    Dark
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />

        {profile && (
          <DropdownMenuItem onClick={handleLogOut}>
            Log out
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Add Avatar component as a property of UserSettingsDropdown
UserSettingsDropdown.Avatar = UserAvatar;

// ============================================================================
// Nav User Component
// ============================================================================
function NavUser({ profile }: { profile?: User }): React.JSX.Element {
  return (
    <SidebarGroup>
      <SidebarMenu>
        <SidebarMenuItem>
          <UserSettingsDropdown
            profile={profile}
            trigger={
              profile && (
                <SidebarMenuButton className="-ml-1.5 transition-none data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground group-data-[collapsible=icon]:ml-0 group-data-[collapsible=icon]:rounded-full group-data-[collapsible=icon]:!p-1">
                  <UserAvatar profile={profile} />
                  <span className="truncate text-sm font-medium leading-none">
                    {profile.name}
                  </span>
                </SidebarMenuButton>
              )
            }
          />
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarGroup>
  );
}

// ============================================================================
// Nav Main Component
// ============================================================================
interface NavMainProps {
  profile?: User;
  isCollapsed?: boolean;
}

function NavMain({ profile, isCollapsed }: NavMainProps): React.JSX.Element {
  const pathname = usePathname();
  const { config: appConfig } = useAppConfigContext();
  return (
    <SidebarGroup>
      <SidebarMenu>
        {capabilityFlags?.organization?.enabled && <div className="my-2">
          <OrganizationsSelect collapsed={isCollapsed} />
        </div>}
        {getFilteredNavItems(appConfig, profile?.role || "user").map((item, index) => (
          <SidebarMenuItem key={index}>
            <SidebarMenuButton
              asChild
              isActive={pathname.startsWith(item.href)}
              tooltip={item.title}
            >
              <Link
                href={item.disabled ? '#' : item.href}
                target={item.external ? '_blank' : undefined}
              >
                {item.icon &&
                  <item.icon
                    className={cn(
                      'size-4 shrink-0',
                      pathname.startsWith(item.href)
                        ? 'text-foreground'
                        : 'text-muted-foreground'
                    )}
                  />
                }
                {item.icon_url && !item.icon && <img src={item.icon_url} className="mr-2 h-4 w-4 dark:brightness-0 dark:invert" />}
                <span
                  className={
                    pathname.startsWith(item.href)
                      ? 'dark:text-foreground'
                      : 'dark:text-muted-foreground'
                  }
                >
                  {item.title}
                </span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}

// ============================================================================
// App Sidebar Component
// ============================================================================
interface AppSidebarProps {
  profile?: User;
}

function AppSidebar({ profile }: AppSidebarProps): React.JSX.Element {
  const sidebar = useSidebar();
  const xlUp = useMediaQuery(MediaQueries.XlUp, { ssr: true, fallback: true });
  const isCollapsed = !sidebar.isMobile && !sidebar.open;
  const showLogo = !isCollapsed || !xlUp;
  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div
          className={cn(
            "flex h-10 w-full flex-row items-center justify-between",
            !isCollapsed && "pl-0.5",
          )}
        >
          {showLogo && (
            <BrandLogo className="truncate transition-[width,height,padding] h-10" />
          )}
          {xlUp && (
            <SidebarTrigger
              buttonIcon={isCollapsed ? "menu" : "chevronLeft"}
              className="shrink-0 rounded-full text-muted-foreground"
            />
          )}
        </div>
      </SidebarHeader>
      <SidebarContent className="overflow-hidden">
        <ScrollArea
          verticalScrollBar
          className="h-full [&>[data-radix-scroll-area-viewport]>div]:flex [&>[data-radix-scroll-area-viewport]>div]:flex-col"
        >
          <NavMain profile={profile} isCollapsed={isCollapsed} />
        </ScrollArea>
      </SidebarContent>
      <SidebarFooter>
        <NavUser profile={profile} />
      </SidebarFooter>
    </Sidebar>
  );
}

// ============================================================================
// Main Navigation Layout
// ============================================================================
export default function MainNavigationLayout({
  children,
}: React.PropsWithChildren): React.JSX.Element {
  const { isLoading, showCopilot, activeOrganizationId, userDto } = useNavigation();

  return (
    <div className="flex h-screen overflow-hidden w-full">
      <SidebarProvider>
        <AppSidebar profile={userDto as User} />
        <SidebarInset
          id="skip"
          className="size-full lg:peer-data-[state=collapsed]:max-w-[calc(100vw-var(--sidebar-width-icon))] lg:peer-data-[state=expanded]:max-w-[calc(100vw-var(--sidebar-width))]"
        >
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
            </div>
          ) : (
            children
          )}
        </SidebarInset>
      </SidebarProvider>
      {showCopilot && <CopilotPopupComponent userId={userDto?.id} organizationId={activeOrganizationId} />}
    </div>
  );
}
